import { Arguments, SWRConfiguration, SWRResponse } from 'swr'
import { useDebounce } from 'use-debounce'
import { useSWRPlus } from '../../services/swrPlus'
import stableHash from 'stable-hash'

export type UseAPIConfig<TData, TError> = SWRConfiguration<TData, TError> & {
  debounceInterval?: number
}
export type UseAPIOptions<TOptions> = TOptions | (() => TOptions | undefined)
export type UseAPIResponse<TData, TError> = SWRResponse<TData, TError>

function normalizeOptions<TOptions extends Arguments = Arguments>(
  uuid: string,
  options: UseAPIOptions<TOptions>
): [string, TOptions] | undefined {
  let normalizedOptions: TOptions | undefined
  if (typeof options === 'function') {
    try {
      normalizedOptions = options()
    } catch (_error) {
      // useSWR expects that if this throws an error it's because
      // the deps aren't ready so it suppresses the error
      // and simply does not call the fetcher yet
      normalizedOptions = undefined
    }
  } else {
    normalizedOptions = options
  }
  if (!normalizedOptions) return
  return [uuid, normalizedOptions]
}

/**
 * Wraps useSWR with helpful boilerplate for calling api getters.
 *
 * @deprecated use `useAPISWR` instead for a simpler interface with less boilerplate
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useAPI<TOptions extends Arguments = Arguments, TData = any, TError = any>(
  uuid: string,
  fetcher: (options: TOptions) => Promise<TData>,
  options: UseAPIOptions<TOptions>,
  config?: UseAPIConfig<TData, TError>
): UseAPIResponse<TData, TError> {
  const [debouncedOptions] = useDebounce(
    normalizeOptions<TOptions>(uuid, options),
    config?.debounceInterval || 0,
    {
      leading: false,
      trailing: true,
      equalityFn(left, right) {
        // stable hash is the same comparator that useSWR uses internally
        // unlike json, it supports functions and circular refs so any args can be used
        return stableHash(left) === stableHash(right)
      }
    }
  )

  return useSWRPlus<TData, TError>(
    debouncedOptions,
    ([_uuid, options]: [string, TOptions]) => fetcher(options),
    config
  )
}
